import DynamicSectionElement from '@/js/dynamic-section-element';

/**
 * Keeps the "Constituent Products" section in sync with the selected variant.
 *
 * The constituent products metafield lives at the variant level, so when the
 * product has multiple variants we re-render this section (via the Section
 * Rendering API) whenever a variant option changes in the product form.
 */
// Matches the `--large-up` custom media breakpoint used in the stylesheet.
const DESKTOP_MEDIA_QUERY = '(min-width: 990px)';

class ConstituentProducts extends DynamicSectionElement {
  connectedCallback() {
    this.desktopMediaQuery = globalThis.matchMedia(DESKTOP_MEDIA_QUERY);
    this.onDesktopMediaChange = this.onDesktopMediaChange.bind(this);
    this.desktopMediaQuery.addEventListener(
      'change',
      this.onDesktopMediaChange
    );

    // Block summary activation on desktop, where the row stays expanded.
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.setupSummaries();

    // On mobile, keep the rows behaving like an accordion (one open at a time).
    this.onRowToggle = this.onRowToggle.bind(this);
    this.setupRows();

    // Expand every row on desktop and collapse them on mobile / tablet.
    this.syncRowsToViewport();

    if (!this.dataset.variants) return;

    this.variants = JSON.parse(this.dataset.variants);
    this.onVariantOptionChange = this.onVariantOptionChange.bind(this);

    // The variant option inputs live in the product form section, so we listen
    // at the document level and filter for the relevant inputs.
    document.addEventListener('change', this.onVariantOptionChange);
  }

  disconnectedCallback() {
    if (this.desktopMediaQuery && this.onDesktopMediaChange) {
      this.desktopMediaQuery.removeEventListener(
        'change',
        this.onDesktopMediaChange
      );
    }

    if (this.onVariantOptionChange) {
      document.removeEventListener('change', this.onVariantOptionChange);
    }
  }

  onDesktopMediaChange() {
    this.syncRowsToViewport();
  }

  /**
   * Prevent the summary from toggling the row while it is permanently
   * expanded on desktop. A keyboard Enter/Space on <summary> dispatches a
   * click, so cancelling the default action covers pointer and keyboard.
   * @param {Event} event
   */
  onSummaryClick(event) {
    if (this.desktopMediaQuery.matches) {
      event.preventDefault();
    }
  }

  /**
   * Attach the click guard to every row summary.
   */
  setupSummaries() {
    const summaries = this.querySelectorAll(
      '[data-constituent-product-summary]'
    );

    for (const summary of summaries) {
      summary.addEventListener('click', this.onSummaryClick);
    }
  }

  /**
   * Attach the accordion toggle handler to every row.
   */
  setupRows() {
    const rows = this.querySelectorAll('[data-constituent-product-row]');

    for (const row of rows) {
      row.addEventListener('toggle', this.onRowToggle);
    }
  }

  /**
   * On mobile, collapse every other row when one is opened so only a single
   * row stays expanded at a time. Desktop keeps all rows permanently open.
   * @param {Event} event
   */
  onRowToggle(event) {
    if (this.desktopMediaQuery.matches) return;
    if (!event.target.open) return;

    const rows = this.querySelectorAll('[data-constituent-product-row]');

    for (const row of rows) {
      if (row !== event.target) {
        row.open = false;
      }
    }
  }

  /**
   * Open every constituent product row on desktop, close them otherwise.
   *
   * On desktop the summary is also removed from the tab order so it can't be
   * keyboard-toggled, keeping the row permanently expanded.
   */
  syncRowsToViewport() {
    const isDesktop = this.desktopMediaQuery.matches;
    const rows = this.querySelectorAll('[data-constituent-product-row]');

    for (const row of rows) {
      row.open = isDesktop;

      const summary = row.querySelector('[data-constituent-product-summary]');
      if (!summary) continue;

      if (isDesktop) {
        summary.setAttribute('tabindex', '-1');
      } else {
        summary.removeAttribute('tabindex');
      }
    }
  }

  /**
   * @param {Event} event
   */
  onVariantOptionChange(event) {
    if (!event.target.closest('[data-option]')) return;

    const matchingVariant = this.getMatchingVariant();
    if (!matchingVariant) return;

    const { origin, pathname } = globalThis.location;
    const parameters = new URLSearchParams({ variant: matchingVariant.id });

    this.loadSectionFromUrl(`${origin}${pathname}?${parameters}`);
  }

  /**
   * Get the currently selected value for a given option position.
   * @param {number} position
   * @returns {string|undefined}
   */
  getSelectedOptionValue(position) {
    const input = document.querySelector(
      `[data-option="${position}"] input[type="radio"]:checked`
    );
    return input ? input.value : undefined;
  }

  /**
   * Find the variant that matches the currently selected options.
   * @returns {Object|undefined}
   */
  getMatchingVariant() {
    const option1 = this.getSelectedOptionValue(1);
    const option2 = this.getSelectedOptionValue(2);
    const option3 = this.getSelectedOptionValue(3);

    return this.variants.find(variant => {
      const option1IsMatch = variant.option1
        ? variant.option1 == option1
        : true;
      const option2IsMatch = variant.option2
        ? variant.option2 == option2
        : true;
      const option3IsMatch = variant.option3
        ? variant.option3 == option3
        : true;

      return option1IsMatch && option2IsMatch && option3IsMatch;
    });
  }
}

customElements.get('constituent-products-section') ||
  customElements.define('constituent-products-section', ConstituentProducts);

export default ConstituentProducts;
